/**
 * SalesRegister.tsx  —  Sales Register Report
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { DataGrid, GridColDef, GridRenderCellParams, GridColumnVisibilityModel } from "@mui/x-data-grid";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import AccountBalanceIcon      from "@mui/icons-material/AccountBalance";
import AssessmentIcon          from "@mui/icons-material/Assessment";
import CheckCircleIcon         from "@mui/icons-material/CheckCircle";
import ClearIcon               from "@mui/icons-material/Clear";
import DownloadIcon            from "@mui/icons-material/Download";
import FilterListIcon          from "@mui/icons-material/FilterList";
import PictureAsPdfIcon        from "@mui/icons-material/PictureAsPdf";
import RefreshIcon             from "@mui/icons-material/Refresh";
import SearchIcon              from "@mui/icons-material/Search";
import TrendingUpIcon          from "@mui/icons-material/TrendingUp";
import WarningAmberIcon        from "@mui/icons-material/WarningAmber";

import Footer from "../Footer/Footer";
import { getUsers } from "../Services/userService";
import {
  getSalesRegister
} from "../Services/InvoicePageService";
import { ISalesRegisterFilters, ISalesRegisterRow, ISalesRegisterSummary } from "../Interfaces/IInvoice";
import { getOrganizationDetails } from "../Services/orgService";
import { IOrgDetails } from "../Interfaces/IorgProfile";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtINR = (n: any) =>
  `Rs. ${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const fmtDate = (s?: string): string => {
  if (!s) return "—";
  try { return format(parseISO(s), "dd/MM/yyyy"); } catch { return s; }
};

const statusChip = (s: string) => {
  const map: Record<string, { label: string; color: "success" | "warning" | "error" | "default" }> = {
    paid:   { label: "Paid",   color: "success" },
    due:    { label: "Due",    color: "warning" },
    cancel: { label: "Cancelled", color: "error" },
  };
  const cfg = map[s] ?? { label: s || "Draft", color: "default" };
  return <Chip label={cfg.label} size="small" color={cfg.color} variant="outlined" sx={{ fontSize: 11 }} />;
};

// ─── Summary Card ─────────────────────────────────────────────────────────────

interface SummaryCardProps {
  label: string; value: string; sub?: string;
  color: string; border: string; icon: React.ReactNode; loading: boolean;
}
const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, sub, color, border, icon, loading }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, borderColor: border, height: "100%" }}>
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      {loading ? (
        <>
          <Skeleton width="60%" height={16} />
          <Skeleton width="80%" height={28} sx={{ mt: 0.5 }} />
        </>
      ) : (
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            <Typography variant="h6" fontWeight={700} color={color}>{value}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box sx={{ opacity: 0.65, mt: 0.5 }}>{icon}</Box>
        </Stack>
      )}
    </CardContent>
  </Card>
);

// ─── Mobile Row Card ──────────────────────────────────────────────────────────

const MobileCard: React.FC<{ row: ISalesRegisterRow; sno: number }> = ({ row, sno }) => (
  <Card
    variant="outlined"
    sx={{
      mb: 1.5, borderRadius: 2,
      borderLeft: "4px solid",
      borderLeftColor: row.status === "paid" ? "success.main" : row.dueAmount > 0 ? "warning.main" : "grey.300",
      "&:hover": { boxShadow: 2 },
    }}
  >
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" sx={{ bgcolor: "grey.100", px: 0.8, py: 0.2, borderRadius: 1, fontWeight: 600, color: "text.secondary" }}>
            #{sno}
          </Typography>
          <Typography variant="body2" fontWeight={700} color="primary.main">{row.invoiceId}</Typography>
        </Stack>
        {statusChip(row.status)}
      </Stack>
      <Typography variant="body2" fontWeight={500} noWrap mb={0.5}>{row.customerName}</Typography>
      <Stack direction="row" spacing={1} mb={1}>
        <Typography variant="caption" color="text.secondary">{fmtDate(row.invoiceDate)}</Typography>
        {row.invoiceType && (
          <Chip label={row.invoiceType.replace("Invoice", "")} size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
        )}
      </Stack>
      <Divider sx={{ my: 1 }} />
      <Grid container spacing={1}>
        {[
          { l: "Taxable",  v: fmtINR(row.taxableAmount) },
          { l: "Total",    v: fmtINR(row.grandTotal),    bold: true },
          { l: "Received", v: fmtINR(row.totalPaidAmount), color: "success.main" },
          { l: "Due",      v: fmtINR(row.dueAmount),     color: row.dueAmount > 0 ? "error.main" : "success.main" },
        ].map(({ l, v, bold, color }) => (
          <Grid item xs={3} key={l}>
            <Typography variant="caption" color="text.secondary" display="block">{l}</Typography>
            <Typography variant="caption" fontWeight={bold ? 700 : 500} color={color ?? "text.primary"}>{v}</Typography>
          </Grid>
        ))}
      </Grid>
    </CardContent>
  </Card>
);

// ─── PDF Export ───────────────────────────────────────────────────────────────

function exportPDF(
  rows: ISalesRegisterRow[],
  summary: ISalesRegisterSummary,
  orgDetails: any,
  filters: ISalesRegisterFilters
) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const W   = doc.internal.pageSize.width;
  const H   = doc.internal.pageSize.height;

  // ── Professional header ──
  // Purple banner
  doc.setFillColor(102, 51, 153);
  doc.rect(0, 0, W, 28, "F");

  doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
  doc.text(orgDetails?.OrgName || "Organisation", 14, 11);

  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  const addr = [
    orgDetails?.address?.[0]?.addressLine1,
    orgDetails?.address?.[0]?.city,
    orgDetails?.address?.[0]?.state,
    orgDetails?.address?.[0]?.zipCode,
  ].filter(Boolean).join(", ");
  doc.text(addr || "", 14, 17);
  doc.text(`GSTIN: ${orgDetails?.GST || "N/A"}   |   ${orgDetails?.email || ""}   |   ${orgDetails?.phone || ""}`, 14, 22);

  // Report title (right side of banner)
  doc.setFontSize(14); doc.setFont("helvetica", "bold");
  doc.text("SALES REGISTER REPORT", W - 14, 11, { align: "right" });
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  const dateRange = [filters.fromDate, filters.toDate].filter(Boolean).join(" to ") || "All dates";
  doc.text(`Period: ${dateRange}`, W - 14, 17, { align: "right" });
  doc.text(`Generated: ${format(new Date(), "dd/MM/yyyy HH:mm")}`, W - 14, 22, { align: "right" });

  doc.setTextColor(0);

  // ── Summary row ──
  let y = 34;
  doc.setFontSize(9); doc.setFont("helvetica", "bold");
  const sumCards = [
    { l: "Total Invoices", v: summary.totalInvoices.toString() },
    { l: "Total Sales",    v: fmtINR(summary.totalSales)     },
    { l: "Total Received", v: fmtINR(summary.totalReceived)  },
    { l: "Total Due",      v: fmtINR(summary.totalDue)       },
  ];
  const cardW = (W - 28) / 4;
  sumCards.forEach((c, i) => {
    const x = 14 + i * cardW;
    doc.setDrawColor(200, 200, 200);
    doc.rect(x, y, cardW - 3, 14);
    doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(100);
    doc.text(c.l, x + 2, y + 5);
    doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(0);
    doc.text(c.v, x + 2, y + 12);
  });

  doc.setTextColor(0);
  y = 54;

  // ── Table ──
  const head = [
    ["#", "Invoice No", "Date", "Customer", "Type", "Items",
     "Taxable", "CGST", "SGST", "IGST", "Disc", "Grand Total",
     "Received", "Due", "Status", "Pay Mode", "Salesperson"],
  ];

  const body = rows.map((r, i) => [
    i + 1,
    r.invoiceId,
    fmtDate(r.invoiceDate),
    r.customerName,
    r.invoiceType?.replace("Invoice", "") ?? "",
    r.itemCount,
    Number(r.taxableAmount).toFixed(2),
    Number(r.cgst).toFixed(2),
    Number(r.sgst).toFixed(2),
    Number(r.igst).toFixed(2),
    Number(r.totalDiscount).toFixed(2),
    Number(r.grandTotal).toFixed(2),
    Number(r.totalPaidAmount).toFixed(2),
    Number(r.dueAmount).toFixed(2),
    r.status,
    r.paymentMode,
    r.salesPersonName,
  ]);

  autoTable(doc, {
    head,
    body,
    startY: y,
    theme:  "grid",
    styles:       { fontSize: 7, cellPadding: 1.5, textColor: [30, 30, 30] },
    headStyles:   { fillColor: [102, 51, 153], textColor: 255, fontStyle: "bold", fontSize: 7.5 },
    alternateRowStyles: { fillColor: [249, 245, 255] },
    columnStyles: {
      0:  { cellWidth: 8,  halign: "center" },
      1:  { cellWidth: 28 },
      2:  { cellWidth: 18 },
      3:  { cellWidth: 30 },
      4:  { cellWidth: 16 },
      5:  { cellWidth: 10, halign: "center" },
      6:  { cellWidth: 18, halign: "right" },
      7:  { cellWidth: 14, halign: "right" },
      8:  { cellWidth: 14, halign: "right" },
      9:  { cellWidth: 14, halign: "right" },
      10: { cellWidth: 12, halign: "right" },
      11: { cellWidth: 20, halign: "right", fontStyle: "bold" },
      12: { cellWidth: 18, halign: "right" },
      13: { cellWidth: 18, halign: "right" },
      14: { cellWidth: 15, halign: "center" },
      15: { cellWidth: 16 },
      16: { cellWidth: 20 },
    },
    didDrawPage: (data) => {
      // Footer on every page
      doc.setFontSize(7); doc.setTextColor(150);
      doc.text(
        `Page ${data.pageNumber} of ${doc.getNumberOfPages()}  |  ${orgDetails?.OrgName || ""}  |  SALES REGISTER REPORT`,
        W / 2, H - 4, { align: "center" }
      );
      doc.setTextColor(0);
    },
  });

  doc.save(`SalesRegister_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
}

// ─── CSV Export ───────────────────────────────────────────────────────────────

function exportCSV(rows: ISalesRegisterRow[]) {
  const headers = [
    "S.No", "Invoice No", "Invoice Date", "Due Date", "Customer Name",
    "Mobile", "GSTIN", "Invoice Type", "Item Count",
    "Taxable Amt", "CGST", "SGST", "IGST", "Discount",
    "Grand Total", "Received", "Due Amount", "Status", "Payment Mode", "Salesperson",
  ];
  const data = rows.map((r, i) => [
    i + 1, r.invoiceId, fmtDate(r.invoiceDate), fmtDate(r.dueDate),
    r.customerName, r.phone ?? "", r.GSTIN ?? "", r.invoiceType,
    r.itemCount, r.taxableAmount, r.cgst, r.sgst, r.igst, r.totalDiscount,
    r.grandTotal, r.totalPaidAmount, r.dueAmount, r.status, r.paymentMode, r.salesPersonName,
  ]);
  const csv = [headers, ...data].map((row) => row.map((c) => `"${c ?? ""}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `SalesRegister_${format(new Date(), "yyyyMMdd")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: "all",  label: "All Statuses" },
  { value: "due",  label: "Pending / Due" },
  { value: "paid", label: "Paid" },
];

const TYPE_OPTIONS = [
  { value: "all",             label: "All Types" },
  { value: "Original",        label: "Original Invoice" },
  { value: "GSTInvoice",      label: "GST Invoice" },
  { value: "QuickInvoice",    label: "Quick Invoice" },
  { value: "PerformaInvoice", label: "Performa Invoice" },
];

interface Customer { id: string; Id: string; customerName?: string; companyName?: string; }

// ─── Main Component ───────────────────────────────────────────────────────────

const SalesRegister: React.FC = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [orgDetails, setOrgDetails] = useState<IOrgDetails|null>(null);
  const orgId     = localStorage.getItem("orgId")  ?? "";
  //const orgDetails= JSON.parse(localStorage.getItem("orgDetails") || "{}");
  
  // ── Data ─────────────────────────────────────────────────────────────────
  const [rows,       setRows]       = useState<ISalesRegisterRow[]>([]);
  const [summary,    setSummary]    = useState<ISalesRegisterSummary>({ totalSales: 0, totalReceived: 0, totalDue: 0, totalInvoices: 0 });
  const [totalCount, setTotalCount] = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  // ── Customers (for filter dropdown) ──────────────────────────────────────
  const [customers,       setCustomers]       = useState<Customer[]>([]);
  const [selectedCustomer,setSelectedCustomer]= useState<Customer | null>(null);

  // ── Filters ───────────────────────────────────────────────────────────────
  const [search,       setSearch]       = useState("");
  const [status,       setStatus]       = useState("all");
  const [invoiceType,  setInvoiceType]  = useState("all");
  const [fromDate,     setFromDate]     = useState("");
  const [toDate,       setToDate]       = useState("");
  const [showFilters,  setShowFilters]  = useState(!isMobile);

  // ── Pagination + sort ────────────────────────────────────────────────────
  const [page,       setPage]       = useState(1);
  const [pageSize,   setPageSize]   = useState(20);
  const [sortField,  setSortField]  = useState("invoiceDate");
  const [sortOrder,  setSortOrder]  = useState<"asc"|"desc">("desc");

  // ── DataGrid column visibility ───────────────────────────────────────────
  const [colVisibility, setColVisibility] = useState<GridColumnVisibilityModel>({
    phone:          false,
    GSTIN:          false,
    dueDate:        false,
    orderId:        false,
    shippingCharge: false,
  });

  const hasFilters = !!(search || (status !== "all") || (invoiceType !== "all") || fromDate || toDate || selectedCustomer);
  const serialOffset = (page - 1) * pageSize;

  // ── Fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const run = async () => {
      if (!orgId) return;
  
      try {
        const data = await getOrganizationDetails(orgId);
        setOrgDetails(data);
      } catch (err) {
        console.error("Failed to load org details:", err);
  
        // fallback (your existing logic)
        const s = localStorage.getItem("orgDetails");
        if (s) setOrgDetails(JSON.parse(s));
      }
    };
  
    run();
  }, [orgId]);
  const fetchData = useCallback(async () => {
    if (!orgId) return;
    setLoading(true); setError("");
    try {
      const data = await getSalesRegister(orgId, {
        fromDate:    fromDate      || undefined,
        toDate:      toDate        || undefined,
        customerId:  selectedCustomer?.Id || selectedCustomer?.id || undefined,
        status:      status        !== "all" ? status      : undefined,
        invoiceType: invoiceType   !== "all" ? invoiceType : undefined,
        search:      search.trim() || undefined,
        sortField,
        sortOrder,
        page,
        limit: pageSize,
      });
      setRows(data.invoices ?? []);
      setSummary(data.summary ?? { totalSales: 0, totalReceived: 0, totalDue: 0, totalInvoices: 0 });
      setTotalCount(data.pagination?.total ?? 0);
    } catch (err: any) {
      setError(err?.message || "Failed to load sales register.");
    } finally {
      setLoading(false);
    }
  }, [orgId, fromDate, toDate, selectedCustomer, status, invoiceType, search, sortField, sortOrder, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load customers for filter
  useEffect(() => {
    if (!orgId) return;
    getUsers(orgId, "customer")
      .then((d) => setCustomers(d ?? []))
      .catch(console.error);
  }, [orgId]);

  // Reset page on filter change
  const resetPage = () => setPage(1);

  const clearFilters = () => {
    setSearch(""); setStatus("all"); setInvoiceType("all");
    setFromDate(""); setToDate(""); setSelectedCustomer(null);
    resetPage();
  };

  // ── DataGrid columns ──────────────────────────────────────────────────────
  const columns: GridColDef[] = [
    {
      field: "__sno__", headerName: "S.No", width: 58, sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {serialOffset + rows.findIndex((r) => r.invoiceId === p.row.invoiceId) + 1}
        </Typography>
      ),
    },
    {
      field: "invoiceId", headerName: "Invoice No", width: 170,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600} color="primary.main" fontSize={12}>
          {p.value}
        </Typography>
      ),
    },
    {
      field: "invoiceDate", headerName: "Invoice Date", width: 105,
      valueFormatter: (v: any) => fmtDate(v),
    },
    { field: "dueDate", headerName: "Due Date", width: 100, valueFormatter: (v: any) => fmtDate(v) },
    { field: "customerName", headerName: "Customer Name", flex: 1, minWidth: 160 },
    { field: "phone",  headerName: "Mobile No",   width: 115 },
    { field: "GSTIN",  headerName: "GSTIN",        width: 145 },
    { field: "orderId",headerName: "Order No",     width: 120 },
    {
      field: "invoiceType", headerName: "Type", width: 110,
      renderCell: (p: GridRenderCellParams) => (
        <Chip label={p.value?.replace("Invoice", "") ?? "—"} size="small" variant="outlined" sx={{ fontSize: 10 }} />
      ),
    },
    { field: "itemCount", headerName: "Items", width: 65, type: "number", headerAlign: "center", align: "center" },
    { field: "taxableAmount", headerName: "Taxable Amt", width: 115, type: "number", headerAlign: "right", align: "right", valueFormatter: (v: any) => Number(v ?? 0).toFixed(2) },
    { field: "cgst",          headerName: "CGST",         width: 90,  type: "number", headerAlign: "right", align: "right", valueFormatter: (v: any) => Number(v ?? 0).toFixed(2) },
    { field: "sgst",          headerName: "SGST",         width: 90,  type: "number", headerAlign: "right", align: "right", valueFormatter: (v: any) => Number(v ?? 0).toFixed(2) },
    { field: "igst",          headerName: "IGST",         width: 90,  type: "number", headerAlign: "right", align: "right", valueFormatter: (v: any) => Number(v ?? 0).toFixed(2) },
    { field: "totalDiscount", headerName: "Discount",     width: 95,  type: "number", headerAlign: "right", align: "right", valueFormatter: (v: any) => Number(v ?? 0).toFixed(2) },
    { field: "shippingCharge",headerName: "Shipping",     width: 85,  type: "number", headerAlign: "right", align: "right", valueFormatter: (v: any) => Number(v ?? 0).toFixed(2) },
    {
      field: "grandTotal", headerName: "Grand Total", width: 120, type: "number", headerAlign: "right", align: "right",
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={700} fontSize={12}>{fmtINR(p.value)}</Typography>
      ),
    },
    {
      field: "totalPaidAmount", headerName: "Received", width: 110, type: "number", headerAlign: "right", align: "right",
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600} fontSize={12} color="success.main">{fmtINR(p.value)}</Typography>
      ),
    },
    {
      field: "dueAmount", headerName: "Due Amount", width: 110, type: "number", headerAlign: "right", align: "right",
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600} fontSize={12} color={Number(p.value) > 0 ? "error.main" : "success.main"}>
          {fmtINR(p.value)}
        </Typography>
      ),
    },
    {
      field: "status", headerName: "Status", width: 100,
      renderCell: (p: GridRenderCellParams) => statusChip(p.value),
    },
    { field: "paymentMode",    headerName: "Pay Mode",    width: 100 },
    { field: "salesPersonName",headerName: "Salesperson", width: 130 },
  ];

  const totalPages = Math.ceil(totalCount / pageSize);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="page-location">
        <span>Reports &gt; Sales Register</span>
      </div>

      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 }, py: 1 }}>

        {/* ── Page Header ────────────────────────────────────────────────── */}
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1} mb={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AssessmentIcon color="primary" fontSize={isMobile ? "small" : "medium"} />
            <Box>
              <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={700}>
                Sales Register Report
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totalCount > 0 ? `${totalCount} invoices · Page ${page} of ${totalPages}` : "Loading…"}
              </Typography>
            </Box>
          </Stack>

          {/* Export buttons */}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Tooltip title="Export current page to PDF">
              <Button
                size="small" variant="outlined" color="error"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => exportPDF(rows, summary, orgDetails, { fromDate, toDate })}
                disabled={loading || rows.length === 0}
              >
                {isMobile ? "PDF" : "Export PDF"}
              </Button>
            </Tooltip>
            <Tooltip title="Export current page to Excel/CSV">
              <Button
                size="small" variant="outlined" color="success"
                startIcon={<DownloadIcon />}
                onClick={() => exportCSV(rows)}
                disabled={loading || rows.length === 0}
              >
                {isMobile ? "CSV" : "Export Excel"}
              </Button>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={fetchData}><RefreshIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* ── Error ──────────────────────────────────────────────────────── */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}
            action={<Button size="small" onClick={fetchData}>Retry</Button>}>
            {error}
          </Alert>
        )}

        {/* ── Summary Cards ──────────────────────────────────────────────── */}
        <Grid container spacing={1.5} mb={2}>
          {[
            { label: "Total Invoices",  value: summary.totalInvoices.toLocaleString("en-IN"),   sub: "in current view", color: "primary.main",  border: "primary.light",  icon: <AssessmentIcon color="primary" /> },
            { label: "Total Sales",     value: fmtINR(summary.totalSales),                      sub: "gross amount",    color: "primary.main",  border: "primary.light",  icon: <TrendingUpIcon color="primary" /> },
            { label: "Total Received",  value: fmtINR(summary.totalReceived),                   sub: "payments in",     color: "success.main",  border: "success.light",  icon: <CheckCircleIcon color="success" /> },
            { label: "Total Due",       value: fmtINR(summary.totalDue),                        sub: "outstanding",     color: summary.totalDue > 0 ? "error.main" : "success.main", border: summary.totalDue > 0 ? "error.light" : "success.light", icon: <WarningAmberIcon color={summary.totalDue > 0 ? "error" : "success"} /> },
          ].map((c) => (
            <Grid item xs={6} sm={3} key={c.label}>
              <SummaryCard {...c} loading={loading} />
            </Grid>
          ))}
        </Grid>

        {/* ── Search + Filter Toggle ─────────────────────────────────────── */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} mb={1.5}>
          <TextField
            size="small"
            placeholder="Search invoice, customer, order…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetPage(); }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearch(""); resetPage(); }}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ flex: 1, maxWidth: { sm: 380 } }}
          />
          <Button
            size="small" startIcon={<FilterListIcon />}
            variant={hasFilters ? "contained" : "outlined"}
            color={hasFilters ? "primary" : "inherit"}
            onClick={() => setShowFilters((v) => !v)}
            sx={{ whiteSpace: "nowrap" }}
          >
            {showFilters ? "Hide Filters" : `Filters${hasFilters ? " ●" : ""}`}
          </Button>
        </Stack>

        {/* ── Filter Panel ───────────────────────────────────────────────── */}
        <Collapse in={showFilters}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "grey.50" }}>
            <Grid container spacing={1.5} alignItems="flex-end">

              {/* Customer */}
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  size="small"
                  options={customers}
                  getOptionLabel={(c) => c.customerName ?? c.companyName ?? ""}
                  value={selectedCustomer}
                  onChange={(_, v) => { setSelectedCustomer(v); resetPage(); }}
                  isOptionEqualToValue={(o, v) => (o.Id || o.id) === (v.Id || v.id)}
                  renderInput={(params) => <TextField {...params} label="Customer" />}
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={status} label="Status"
                    onChange={(e: SelectChangeEvent) => { setStatus(e.target.value); resetPage(); }}>
                    {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              {/* Invoice Type */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Invoice Type</InputLabel>
                  <Select value={invoiceType} label="Invoice Type"
                    onChange={(e: SelectChangeEvent) => { setInvoiceType(e.target.value); resetPage(); }}>
                    {TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              {/* From Date — same height as other controls */}
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="From Date" type="date" size="small" fullWidth
                  value={fromDate}
                  onChange={(e) => { setFromDate(e.target.value); resetPage(); }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: toDate || undefined }}
                />
              </Grid>

              {/* To Date */}
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="To Date" type="date" size="small" fullWidth
                  value={toDate}
                  onChange={(e) => { setToDate(e.target.value); resetPage(); }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: fromDate || undefined }}
                />
              </Grid>

              {/* Clear */}
              {hasFilters && (
                <Grid item xs={12} sm={6} md={1}>
                  <Button variant="outlined" color="error" size="small"
                    startIcon={<ClearIcon />} onClick={clearFilters} fullWidth>
                    Clear
                  </Button>
                </Grid>
              )}
            </Grid>

            {/* Rows per page selector inside filter panel */}
            <Stack direction="row" alignItems="center" spacing={1} mt={1.5}>
              <Typography variant="caption" color="text.secondary">Rows per page:</Typography>
              <Select
                size="small" value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); resetPage(); }}
                sx={{ fontSize: 13, minWidth: 75 }}
              >
                {[10, 20, 50, 100].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
              {totalCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Showing {serialOffset + 1}–{Math.min(serialOffset + pageSize, totalCount)} of {totalCount}
                </Typography>
              )}
            </Stack>
          </Paper>
        </Collapse>

        {/* ── Mobile: Card list ──────────────────────────────────────────── */}
        {isMobile ? (
          <>
            {loading
              ? [1,2,3,4,5].map((k) => <Skeleton key={k} variant="rounded" height={150} sx={{ mb: 1.5, borderRadius: 2 }} />)
              : rows.length === 0
                ? (
                  <Box textAlign="center" py={6}>
                    <AssessmentIcon sx={{ fontSize: 48, color: "grey.300", mb: 1 }} />
                    <Typography color="text.secondary">No invoices found</Typography>
                    {hasFilters && <Button size="small" onClick={clearFilters} sx={{ mt: 1 }}>Clear filters</Button>}
                  </Box>
                )
                : rows.map((row, idx) => (
                    <MobileCard key={row.invoiceId} row={row} sno={serialOffset + idx + 1} />
                  ))
            }
          </>
        ) : (
          /* ── Desktop: DataGrid ─────────────────────────────────────────── */
          <DataGrid
            rows={rows.map((r) => ({ ...r, id: r.invoiceId }))}
            columns={columns}
            getRowId={(r) => r.invoiceId}
            loading={loading}

            // Client-side pagination on the current server page
            // (server already returned exactly pageSize rows)
            paginationMode="client"
            pageSizeOptions={[]}      // hide built-in footer pager — we use custom below
            hideFooterPagination
            hideFooter

            autoHeight
            disableRowSelectionOnClick
            density="compact"
            columnVisibilityModel={colVisibility}
            onColumnVisibilityModelChange={setColVisibility}

            // Sortable — triggers re-fetch via sortField/sortOrder state
            sortingMode="server"
            onSortModelChange={(model) => {
              if (model.length > 0) {
                setSortField(model[0].field);
                setSortOrder(model[0].sort ?? "desc");
              } else {
                setSortField("invoiceDate");
                setSortOrder("desc");
              }
              resetPage();
            }}

            getRowClassName={(p) =>
              p.row.status === "paid"   ? "row-paid"   :
              p.row.dueAmount > 0       ? "row-due"    : ""
            }

            // sx={{
            //   borderRadius: 2,
            //   border: "1px solid",
            //   borderColor: "divider",
            //   "& .MuiDataGrid-columnHeaders": {
            //     bgcolor:     "#663399",
            //     color:       "white",
            //     fontWeight:  700,
            //     fontSize:    12,
            //     "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
            //     "& .MuiDataGrid-sortIcon":    { color: "rgba(255,255,255,0.8)" },
            //     "& .MuiDataGrid-menuIconButton": { color: "rgba(255,255,255,0.8)" },
            //   },
            //   "& .row-paid": { bgcolor: "rgba(46,125,50,0.04)",  "&:hover": { bgcolor: "rgba(46,125,50,0.09)"  } },
            //   "& .row-due":  { bgcolor: "rgba(237,108,2,0.04)",  "&:hover": { bgcolor: "rgba(237,108,2,0.09)"  } },
            //   "& .MuiDataGrid-cell":          { fontSize: 12 },
            //   "& .MuiDataGrid-columnSeparator": { color: "rgba(255,255,255,0.3)" },
            // }}
            sx={{
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",

            "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#4338CA",
                minHeight: "44px !important",
                borderBottom: "none",
            },

            "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#4338CA",
                color: "#FFFFFF",
            },

            "& .MuiDataGrid-columnHeaderTitle": {
                color: "#FFFFFF !important",
                fontWeight: 700,
                fontSize: 12,
            },

            "& .MuiDataGrid-sortIcon": {
                color: "#FFFFFF !important",
                opacity: 1,
            },

            "& .MuiDataGrid-menuIconButton": {
                color: "#FFFFFF !important",
                opacity: 1,
            },

            "& .MuiDataGrid-columnSeparator": {
                color: "rgba(255,255,255,0.2)",
            },

            "& .MuiDataGrid-cell": {
                fontSize: 12,
            },

            "& .row-paid": {
                bgcolor: "rgba(22,163,74,0.05)",
                "&:hover": {
                bgcolor: "rgba(22,163,74,0.10)",
                },
            },

            "& .row-due": {
                bgcolor: "rgba(245,158,11,0.05)",
                "&:hover": {
                bgcolor: "rgba(245,158,11,0.10)",
                },
            },
            }}
          />
        )}
        {/* ── Pagination Footer ──────────────────────────────────────────── */}
        {!loading && totalCount > 0 && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
            mt={1.5}
            px={0.5}
          >
            <Typography variant="caption" color="text.secondary">
              Page {page} of {totalPages} · {totalCount} invoices
              {hasFilters ? " (filtered)" : ""}
            </Typography>

            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => {
                setPage(p);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              color="primary"
              size={isMobile ? "small" : "medium"}
              showFirstButton
              showLastButton
              siblingCount={isMobile ? 0 : 1}
            />

            <Typography variant="caption" color="text.secondary">
              {serialOffset + 1}–{Math.min(serialOffset + pageSize, totalCount)} of {totalCount}
            </Typography>
          </Stack>
        )}
      </Container>

      <Box sx={{ width: "100%", mt: isMobile ? 4 : 6 }}>
        <Footer />
      </Box>
    </>
  );
};

export default SalesRegister;